import { ArrowRight, Sparkles } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppData } from '@/contexts/AppDataContext';

const FloatingLines = lazy(() => import('./FloatingLines'));

const Hero = () => {
  const { language } = useLanguage();
  const { appData } = useAppData();
  const t = appData?.content?.hero?.[language] || {};
  const companyInfo = appData?.company || {};
  const isRTL = language === 'ar';

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate dark:from-dark via-white dark:via-gray-900 to-primary/10 dark:to-primary/5 z-10">
      <div className="absolute inset-0 w-full h-full z-0">
        <Suspense fallback={<div className="w-full h-full bg-gradient-to-br from-primary/10 via-transparent to-primary/5" aria-hidden="true" />}>
          <FloatingLines
            enabledWaves={['top', 'middle', 'bottom']}
            lineCount={[1, 2, 3]}
            lineDistance={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            bendRadius={5.0}
            bendStrength={-0.5}
            interactive={false}
            parallax={false}
            linesGradient={['#c0fc00']}
          />
        </Suspense>
      </div>

      <div className="absolute inset-0 opacity-5 dark:opacity-10 z-0" aria-hidden="true">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full max-w-full overflow-hidden flex items-center justify-center min-h-screen">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
          <div
            className="max-w-6xl w-full h-[90vh] min-h-[600px] rounded-3xl backdrop-blur-md bg-white/30 dark:bg-black/40 border border-white/20 dark:border-white/10 shadow-2xl"
            style={{
              transform: 'scale(0.8)',
              transformOrigin: 'center center'
            }}
          />
        </div>

        <div
          className="max-w-6xl text-center w-full overflow-visible mx-auto relative z-10 p-6 md:p-8 lg:p-12"
          style={{
            transform: 'scale(0.8)',
            transformOrigin: 'center center'
          }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 dark:bg-primary/30 rounded-full mb-8 animate-fade-in-up">
            <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
            <span className="text-sm font-semibold text-dark dark:text-white">{t.badge || 'ROI Agency'}</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-fade-in-up-delay-1">
            {t.title || 'Driving your success, STEP by STEP'}
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto text-balance animate-fade-in-up-delay-2">
            {t.tagline || 'In creativity and technology, we make your ideas a reality. Join us and participate in transforming your project into an exceptional digital experience!'}
          </p>

          <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up-delay-3 ${isRTL ? 'rtl' : ''}`}>
            <a
              href="#contact"
              className={`group px-8 py-4 bg-primary text-dark font-semibold rounded-lg flex items-center gap-2 hover:bg-primary-hover transition-all shadow-lg hover:shadow-xl min-h-[44px] ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              {t.consultation || 'Free consultation'}
              <ArrowRight className={`w-5 h-5 transition-transform ${isRTL ? 'rotate-180' : ''} ${!isRTL ? 'group-hover:translate-x-1' : 'group-hover:-translate-x-1'}`} aria-hidden="true" />
            </a>
            <a
              href="#services"
              className="px-8 py-4 border-2 border-primary text-primary dark:text-primary font-semibold rounded-lg hover:bg-primary hover:text-dark transition-all dark:hover:text-dark min-h-[44px] flex items-center justify-center"
            >
              {t.services || 'our services'}
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 lg:gap-8 w-full animate-fade-in-up-delay-4">
            {[
              { value: '+' + companyInfo.stats?.experience, label: t?.experience || 'Years of experience' },
              { value: '+' + companyInfo.stats?.projects, label: t?.projects || 'Executed projects' },
              { value: '+' + companyInfo.stats?.clients, label: t.clients || 'Number of clients' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-3xl font-bold text-primary mb-1 md:mb-2">
                  {stat.value}
                </div>
                <div className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400 leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
